/**
 * /zh-tw/comparisons/coda — Draftlize vs Coda (Traditional Chinese).
 * Mirrors EN /comparisons/coda. Links to /zh-tw/decision-log.
 */
import { MarketingShell } from '../MarketingShell';

export function CodaZhTw() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/comparisons/coda"
      hreflangLocales={['zhtw']}
      documentTitle="AI 原生產品團隊的 Coda 替代方案｜Draftlize vs Coda · Draftlize"
      documentDescription="Coda 把文件變成應用；但產品決策需要的是連動與漂移偵測。比較 Draftlize 與 Coda 在產品工作上的差異。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">比較 · vs Coda</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 60px)', lineHeight: 1.02, margin: '12px 0 24px' }}>
            Draftlize vs Coda——<br />
            <em>產品團隊該選哪個？</em>
          </h1>
          <p className="lede">
            Coda 把文件變成應用：表格、公式、按鈕、自動化，一份 doc 就能當成小工具用，非常強大。但產品工作真正需要的，不是把 doc 變 app，而是讓<em>決策</em>彼此連動、並在前提改變時被偵測出來。Draftlize 是一個基座，每個決策都是可引用的卡片，改變時自動標記 stale，AI agent 也讀得到。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">重點</span>
            <h2 className="serif">並排比較，<em>7 個面向。</em></h2>
          </div>
          <p>把 doc 變 app，還是讓決策保持最新——解的是不同的問題。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Coda</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>基座形態</td><td className="ours yes">有型別的決策卡片圖</td><td className="partial">doc 加表格與公式</td></tr>
            <tr><td>決策作為可引用的單位</td><td className="ours yes">原生</td><td className="no">寫在 doc 內</td></tr>
            <tr><td>依賴改變時 stale 傳播</td><td className="ours yes">原生</td><td className="no">—</td></tr>
            <tr><td>AI 讀寫決策</td><td className="ours yes">原生（MCP）</td><td className="no">—</td></tr>
            <tr><td>表格・公式・自動化</td><td className="no">—</td><td className="yes">優秀</td></tr>
            <tr><td>把 doc 當互動應用</td><td className="no">—</td><td className="yes">獨到</td></tr>
            <tr><td>團隊協作・權限</td><td className="no">個人優先（v1）</td><td className="yes">優秀</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>Coda 勝出</em>的地方。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">你要把 doc 變成工具</h3>
            <p>表格、公式、按鈕、自動化——用一份 doc 打造內部小工具，Coda 是頂尖的。Draftlize 不打算做這件事。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">你活在結構化資料裡</h3>
            <p>互相連動的表格、視圖、rollup——Coda 的結構化資料能力很強。這類形狀 Coda 勝。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">團隊協作與權限</h3>
            <p>多人即時協作、細緻權限——Coda 為此而建。Draftlize 的設計是個人優先。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>Draftlize 勝出</em>的地方。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">決策是可引用的單位</h3>
            <p>在 Coda，決策散落在 doc 段落裡。在 Draftlize，一個決策就是一張卡片，帶著脈絡與理由，用 ID 就能指出來。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">前提改變時自動 stale</h3>
            <p>改 Coda 的 doc，沒人會注意。改 Draftlize 的決策卡，依賴它的卡片自動標 <code>stale</code>，像建置系統追蹤失效。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 讀得也寫得</h3>
            <p>Coda 的內容是給人讀的。Draftlize 卡片經 MCP 讓 Claude Code、Cursor 讀寫。延伸閱讀 <a href="/zh-tw/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>決策紀錄</a>。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Coda 回答「怎麼把 doc 變成好用的工具」。Draftlize 回答「當 AI 做了一半的工作，產品決策怎麼保持最新」。
        </blockquote>
        <cite>如果你要的是 doc 變 app，Coda 就夠了。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">免費開始，<em>送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Coda 可以繼續用來做 doc 工具。產品決策就交給 Draftlize——變成互相連動、保持最新的卡片。新註冊送 $5、用多少付多少、餘額永不過期。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
